// This file is located in functions/constants/gameData.js

// This file stores the average duration in minutes for a single match of each game.
// It will be used to automatically calculate an event's estimated end time.

// ============================================================================
// GT7 LOBBY SETTINGS — rich per-setting schema
// Shape: { value, label, options, locked?, requires?, description? }
//   value     — current canonical (default) value
//   label     — human-readable label for UI
//   options   — list of selectable strings (host picker contents)
//   locked    — host cannot change (competitive-fairness lock)
//   requires  — only editable when sibling setting equals given value
//   description — optional explanation surfaced in the UI
//
// Use extractLobbyValues(GT7_LOBBY_SETTINGS) to flatten to bare values for
// storage in event docs (preserves the older flat shape so EventDetailScreen
// and existing event docs keep working without migration).
// ============================================================================

export const GT7_LOBBY_SETTINGS = {
    details: {
        roomMode: {
            value: 'Practice/Qualifier/Race',
            label: 'Room Mode',
            options: ['Practice/Qualifier/Race', 'Practice/Qualifier/Endurance Race'],
        },
        roomPrivacy: {
            value: 'Private',
            label: 'Room Privacy',
            options: ['Public', 'Friends Only', 'Private'],
            locked: true,
            description: 'CritCash events always run in private lobbies to keep the field to verified entrants.',
        },
        raceType: {
            value: 'Race for Real',
            label: 'Race Type',
            options: ['Race for Real', 'Drift'],
        },
        // Default is the Heats/Showdown value (15 racers + host spectator = 16).
        // CreateEventsScreen overrides to '2' for H2H mode via useEffect.
        maxParticipants: {
            value: '16 (Host occupies one slot)',
            label: 'Max Participants',
            options: ['2', '16 (Host occupies one slot)'],
            locked: true,
        },
        autoStart: {
            value: 'Off',
            label: 'Auto-Start',
            options: ['On', 'Off'],
            description: 'Host waits until all racers join and match-ready signal fires before starting.',
        },
    },
    timeWeather: {
        weatherSelectionMethod: {
            value: 'Preset Weather',
            label: 'Weather Selection Method',
            options: ['Preset Weather'],
            locked: true,
            description: 'Locked to Preset Weather — Custom Weather introduces variability that conflicts with fair-race rules.',
        },
        presetWeather: {
            value: 'S01 Dry, Cloudless and Pleasant',
            label: 'Preset Weather',
            options: ['S01 Dry, Cloudless and Pleasant'],
            locked: true,
            description: 'Locked to S01 for equal conditions across all races.',
        },
        equalConditionsMode: {
            value: 'On',
            label: 'Equal Conditions Mode',
            options: ['On', 'Off'],
        },
        timeOfDay: {
            value: 'Afternoon',
            label: 'Time of Day',
            options: ['Early Dawn', 'Dawn', 'Sunrise', 'Early Morning', 'Late Morning', 'Afternoon', 'Evening', 'Sunset', 'Twilight', 'Night', 'Midnight'],
        },
        variableTimeSpeedRate: {
            value: '0x',
            label: 'Variable Time Speed Rate',
            options: ['0x', '1x', '2x', '5x', '10x', '15x', '20x', '25x', '30x'],
            requires: { equalConditionsMode: 'Off' },
            description: 'Only editable when Equal Conditions Mode is Off.',
        },
    },
    raceSettings: {
        startType: {
            value: 'Grid Start',
            label: 'Start Type',
            options: ['Grid Start', 'Rolling Start', 'Grid Start with False Start Check'],
        },
        gridOrder: {
            value: 'Fastest First',
            label: 'Grid Order',
            options: ['Fastest First'],
            locked: true,
            description: 'Locked to Fastest First — prevents sandbagging qualifier laps.',
        },
        bopTuningProhibited: {
            value: 'On',
            label: 'BoP/Tuning Prohibited',
            options: ['On'],
            locked: true,
            description: 'Locked On — Balance of Performance enforced for all events.',
        },
        settingsOptions: {
            value: 'All',
            label: 'Settings Options',
            options: ['All'],
            locked: true,
            description: 'Controls suspension, differential, transmission, downforce, anti-lag, brake balance, etc. BoP auto-configures these.',
        },
        boost: {
            value: 'None',
            label: 'Boost',
            options: ['None', 'Weak', 'Strong'],
        },
        slipstreamStrength: {
            value: 'Real',
            label: 'Slipstream Strength',
            options: ['Off', 'Weak', 'Real', 'Strong'],
        },
        visibleDamage: {
            value: 'On',
            label: 'Visible Damage',
            options: ['On', 'Off'],
        },
        mechanicalDamage: {
            value: 'Heavy',
            label: 'Mechanical Damage',
            options: ['Off', 'Weak', 'Heavy'],
            description: 'Heavy = pit stop required to repair.',
        },
        tireWearRate: {
            value: '6x',
            label: 'Tire Wear Rate',
            options: ['Off', '1x', '2x', '3x', '4x', '5x', '6x', '7x', '8x', '9x', '10x', '15x', '20x', '25x', '30x', '40x', '50x'],
        },
        fuelConsumptionRate: {
            value: '6x',
            label: 'Fuel Consumption Rate',
            options: ['Off', '1x', '2x', '3x', '4x', '5x', '6x', '7x', '8x', '9x', '10x', '15x', '20x', '25x', '30x', '40x', '50x'],
        },
        refuelingSpeed: {
            value: '5 L/sec',
            label: 'Refueling Speed',
            options: ['1 L/sec', '2 L/sec', '3 L/sec', '4 L/sec', '5 L/sec', '6 L/sec', '7 L/sec', '8 L/sec', '9 L/sec', '10 L/sec', '12 L/sec', '15 L/sec', '20 L/sec'],
        },
        initialFuel: {
            value: 'Default',
            label: 'Initial Fuel',
            options: ['Default', '1 L', '5 L', '10 L', '25 L', '50 L', '75 L', '100 L'],
        },
        gripReductionOffTrack: {
            value: 'Real',
            label: 'Grip Reduction Off Track',
            options: ['Off', 'Weak', 'Real', 'Strong'],
        },
        raceFinishDelay: {
            value: '180 Seconds',
            label: 'Race Finish Delay',
            options: ['30 Seconds', '60 Seconds', '90 Seconds', '120 Seconds', '150 Seconds', '180 Seconds'],
        },
        nitroOvertakingMultiplier: {
            value: 'Default',
            label: 'Nitro/Overtaking System Usage Time Multiplier',
            options: ['Default', '0.1x', '0.25x', '0.5x', '1x', '2x', '5x', '10x'],
        },
    },
    qualifierSettings: {
        timeLimit: {
            value: '15 Minutes',
            label: 'Time Limit',
            options: ['1 Minute', '5 Minutes', '10 Minutes', '15 Minutes', '30 Minutes', '45 Minutes', '1 Hour', '2 Hours', '4 Hours', '8 Hours', '12 Hours', '24 Hours'],
        },
        qualifyingContinuationTime: {
            value: '180 Seconds',
            label: 'Qualifying Continuation Time',
            options: ['30 Seconds', '60 Seconds', '90 Seconds', '120 Seconds', '150 Seconds', '180 Seconds'],
        },
        tireWearRate: {
            value: 'Same as During the Race',
            label: 'Tire Wear Rate',
            options: ['Same as During the Race', 'Off', '1x', '2x', '3x', '4x', '5x', '6x', '7x', '8x', '9x', '10x'],
        },
        fuelConsumptionRate: {
            value: 'Same as During the Race',
            label: 'Fuel Consumption Rate',
            options: ['Same as During the Race', 'Off', '1x', '2x', '3x', '4x', '5x', '6x', '7x', '8x', '9x', '10x'],
        },
        initialFuel: {
            value: 'Same as During the Race',
            label: 'Initial Fuel',
            options: ['Same as During the Race', 'Default', '1 L', '5 L', '10 L', '25 L', '50 L', '75 L', '100 L'],
        },
        slipstreamStrength: {
            value: 'Real',
            label: 'Slipstream Strength',
            options: ['Off', 'Weak', 'Real', 'Strong'],
        },
    },
    regulationSettings: {
        ppLimit: {
            value: 'No Limit',
            label: 'PP Limit',
            options: ['No Limit', '300', '400', '500', '600', '700', '800', '900', '1000'],
        },
        maxPowerOutput: {
            value: 'No Limit',
            label: 'Max. Power Output',
            options: ['No Limit', '200', '400', '600', '800', '1000', '1200', '1479'],
        },
        minimumWeight: {
            value: 'No Limit',
            label: 'Minimum Weight',
            options: ['No Limit', '1000 lbs', '1500 lbs', '2000 lbs', '2500 lbs', '3000 lbs', '3500 lbs', '4000 lbs', '4409 lbs'],
        },
        usableTires: {
            value: 'Racing',
            label: 'Usable Tires',
            options: ['No Limit', 'Comfort', 'Sports', 'Racing'],
        },
        usableTireTypes: {
            value: 'All',
            label: 'Usable Tire Types',
            options: ['All', 'Hard', 'Medium', 'Soft'],
            multiSelect: true,
            description: 'Pick All, or any combination of Hard/Medium/Soft.',
        },
        requiredTireType: {
            value: 'All',
            label: 'Required Tire Type',
            options: ['All', 'Soft', 'Medium', 'Hard'],
            multiSelect: true,
            description: 'Pick All, or any combination of Soft/Medium/Hard.',
        },
        nitrous: {
            value: 'Prohibited',
            label: 'Nitrous',
            options: ['Prohibited'],
            locked: true,
        },
        kartUsage: {
            value: 'Off',
            label: 'Kart Usage',
            options: ['Off'],
            locked: true,
        },
        engineSwap: {
            value: 'Prohibited',
            label: 'Engine Swap',
            options: ['Prohibited'],
            locked: true,
        },
        tuningParts: {
            value: 'Restricted',
            label: 'Tuning Parts',
            options: ['Unrestricted', 'Restricted'],
        },
        yearLowerLimit: {
            value: 'No Limit',
            label: 'Year (Lower Limit)',
            options: ['No Limit', '1930', '1940', '1950', '1960', '1970', '1980', '1990', '2000', '2010', '2020', '2035'],
        },
        yearUpperLimit: {
            value: 'No Limit',
            label: 'Year (Upper Limit)',
            options: ['No Limit', '1930', '1940', '1950', '1960', '1970', '1980', '1990', '2000', '2010', '2020', '2035'],
        },
        drivetrain: {
            value: 'Unrestricted',
            label: 'Drivetrain',
            options: ['Unrestricted', 'FF', 'FR', 'MR', 'RR', 'AWD'],
        },
        aspiration: {
            value: 'Unrestricted',
            label: 'Aspiration',
            options: ['Unrestricted', 'NA', 'Turbo', 'Supercharger'],
        },
    },
    penaltySettings: {
        shortcutPenalty: {
            value: 'Strong',
            label: 'Shortcut Penalty',
            options: ['Off', 'Weak', 'Strong'],
        },
        wallCollisionPenalty: {
            value: 'Time Penalty (Strong)',
            label: 'Wall Collision Penalty',
            options: ['Off', 'Time Penalty (Weak)', 'Time Penalty (Strong)'],
        },
        correctVehicleCourseAfterWallCollision: {
            value: 'Off',
            label: 'Correct Vehicle Course After Wall Collision',
            options: ['On', 'Off'],
        },
        carCollisionPenalty: {
            value: 'On',
            label: 'Car Collision Penalty',
            options: ['On', 'Off'],
        },
        pitLaneLineCuttingPenalty: {
            value: 'On',
            label: 'Pit Lane Line-Cutting Penalty',
            options: ['On', 'Off'],
        },
        ghostingDuringRace: {
            value: 'Off',
            label: 'Ghosting During Race',
            options: ['Off', 'Weak', 'Strong'],
        },
        flagRules: {
            value: 'On',
            label: 'Flag Rules',
            options: ['On', 'Off'],
        },
    },
    drivingOptionsLimitations: {
        countersteeringAssist: {
            value: 'Prohibited',
            label: 'Countersteering Assist',
            options: ['Prohibited', 'No Limit'],
        },
        activeStabilityManagement: {
            value: 'Prohibited',
            label: 'Active Stability Management (ASM)',
            options: ['Prohibited', 'No Limit'],
        },
        drivingLineAssist: {
            value: 'Prohibited',
            label: 'Driving Line Assist',
            options: ['Prohibited', 'No Limit'],
        },
        tractionControl: {
            value: 'Prohibited',
            label: 'Traction Control',
            options: ['Prohibited', 'No Limit'],
        },
        abs: {
            value: 'No Limit',
            label: 'ABS',
            options: ['Prohibited', 'No Limit'],
        },
        autoDrive: {
            value: 'No Limit',
            label: 'Auto-Drive',
            options: ['Prohibited', 'No Limit'],
        },
    },
};

// Flatten the rich GT7_LOBBY_SETTINGS shape down to bare value strings, matching
// the legacy flat schema used by EventDetailScreen and existing event docs.
//   extractLobbyValues({ raceSettings: { tireWearRate: { value: '6x', ... } } })
//   === { raceSettings: { tireWearRate: '6x' } }
// Pass partial overrides (e.g. { raceSettings: { tireWearRate: '8x' } }) to merge
// host customizations on top of the defaults.
export function extractLobbyValues(settings, overrides = {}) {
    const out = {};
    for (const [sectionKey, sectionData] of Object.entries(settings)) {
        out[sectionKey] = {};
        for (const [settingKey, settingDef] of Object.entries(sectionData)) {
            const overrideVal = overrides?.[sectionKey]?.[settingKey];
            out[sectionKey][settingKey] = overrideVal !== undefined
                ? overrideVal
                : (settingDef && typeof settingDef === 'object' && 'value' in settingDef)
                    ? settingDef.value
                    : settingDef;
        }
    }
    return out;
}

export const GT7_SETTING_SECTION_LABELS = {
    details: 'Details',
    timeWeather: 'Time/Weather',
    raceSettings: 'Race Settings',
    qualifierSettings: 'Qualifier Settings',
    regulationSettings: 'Regulation Settings',
    penaltySettings: 'Penalty Settings',
    drivingOptionsLimitations: 'Driving Options Limitations',
};

export const GT7_SETTING_KEY_LABELS = {
    roomMode: 'Room Mode',
    roomPrivacy: 'Room Privacy',
    raceType: 'Race Type',
    maxParticipants: 'Max Participants',
    autoStart: 'Auto-Start',
    weatherSelectionMethod: 'Weather Selection Method',
    presetWeather: 'Preset Weather',
    equalConditionsMode: 'Equal Conditions Mode',
    timeOfDay: 'Time of Day',
    variableTimeSpeedRate: 'Variable Time Speed Rate',
    startType: 'Start Type',
    gridOrder: 'Grid Order',
    bopTuningProhibited: 'BoP/Tuning Prohibited',
    settingsOptions: 'Settings Options',
    boost: 'Boost',
    slipstreamStrength: 'Slipstream Strength',
    visibleDamage: 'Visible Damage',
    mechanicalDamage: 'Mechanical Damage',
    tireWearRate: 'Tire Wear Rate',
    fuelConsumptionRate: 'Fuel Consumption Rate',
    refuelingSpeed: 'Refueling Speed',
    initialFuel: 'Initial Fuel',
    gripReductionOffTrack: 'Grip Reduction Off Track',
    raceFinishDelay: 'Race Finish Delay',
    nitroOvertakingMultiplier: 'Nitro/Overtaking System Usage Time Multiplier',
    timeLimit: 'Time Limit',
    qualifyingContinuationTime: 'Qualifying Continuation Time',
    filterByCategory: 'Filter by Category',
    ppLimit: 'PP Limit',
    maxPowerOutput: 'Max. Power Output',
    minimumWeight: 'Minimum Weight',
    usableTires: 'Usable Tires',
    usableTireTypes: 'Usable Tire & Types',
    requiredTireType: 'Required Tire Type',
    minimumPitStops: 'Minimum Pit Stops',
    minimumTireChange: 'Minimum Tire Change',
    nitrous: 'Nitrous',
    kartUsage: 'Kart Usage',
    engineSwap: 'Engine Swap',
    tuningParts: 'Tuning Parts',
    yearLowerLimit: 'Year (Lower Limit)',
    yearUpperLimit: 'Year (Upper Limit)',
    drivetrain: 'Drivetrain',
    aspiration: 'Aspiration',
    shortcutPenalty: 'Shortcut Penalty',
    wallCollisionPenalty: 'Wall Collision Penalty',
    correctVehicleCourseAfterWallCollision: 'Correct Vehicle Course After Wall Collision',
    carCollisionPenalty: 'Car Collision Penalty',
    pitLaneLineCuttingPenalty: 'Pit Lane Line-Cutting Penalty',
    ghostingDuringRace: 'Ghosting During Race',
    flagRules: 'Flag Rules',
    countersteeringAssist: 'Countersteering Assist',
    activeStabilityManagement: 'Active Stability Management (ASM)',
    drivingLineAssist: 'Driving Line Assist',
    tractionControl: 'Traction Control',
    abs: 'ABS',
    autoDrive: 'Auto-Drive',
};

// ============================================================================
// MADDEN GAME SETTINGS — per-setting schema (mirror of functions/constants).
// The event creator configures these on CreateEventsScreen; they're stored
// flat on the event doc as ev.gameSettings and editable from EventDetailScreen
// (creator/admin, while the event is still scheduled) via the
// updateEventGameSettings callable.
// Shape: { label, type: 'options'|'minutes', options?, min?, max?, default }
// ============================================================================

export const MADDEN_GAME_SETTINGS = {
    gameSkill: {
        label: 'Game Skill',
        type: 'options',
        options: ['Rookie', 'Pro', 'All Pro', 'All Madden'],
        default: 'All Pro',
    },
    quarterLength: {
        label: 'Quarter Length',
        type: 'minutes',
        min: 1,
        max: 15,
        default: 5,
    },
    acceleratedClock: {
        label: 'Accelerated Clock',
        type: 'options',
        options: ['Off', '25 Secs', '20 Secs', '15 Secs', '10 Secs'],
        default: '20 Secs',
    },
    injuries: {
        label: 'Injuries',
        type: 'options',
        options: ['Off', 'On'],
        default: 'Off',
    },
    evenTeams: {
        label: 'Even Teams',
        type: 'options',
        options: ['Off', 'On'],
        default: 'Off',
    },
    gameStyle: {
        label: 'Game Style',
        type: 'options',
        options: ['Competitive', 'Simulation', 'Arcade'],
        default: 'Competitive',
    },
};

// Default settings object for a fresh Madden event.
export function maddenDefaultSettings() {
    const out = {};
    for (const [key, def] of Object.entries(MADDEN_GAME_SETTINGS)) {
        out[key] = def.default;
    }
    return out;
}

// Rough full-game duration: 4 quarters + halftime/timeouts/pre-game loading.
// 5-min quarters → 35 min, matching the old '5-Min Quarters' format preset.
export function estimateMaddenDurationMinutes(quarterLength) {
    const q = Math.min(15, Math.max(1, Math.floor(Number(quarterLength) || 5)));
    return q * 4 + 15;
}

// Legacy-event bridge: derive a settings object for Madden events created
// before gameSettings existed (they only carried a quarter-length format name).
export function maddenSettingsFromLegacyFormat(formatName) {
    const defaults = maddenDefaultSettings();
    if (String(formatName || '').startsWith('3-Min')) defaults.quarterLength = 3;
    return defaults;
}

// Platform-wide clamp on battle-royale event size while BR scoring is MANUAL:
// adminResolveRace accepts at most 64 placements, so no BR event may seat more
// players than an admin can resolve. Binds EVERYONE, admins included (the
// resolver limit is hard). Lift to 150 when Warzone vision automates scoring.
// Effective cap for a BR event =
//   min(fmt.maxPlayersBySquadSize?.[squadSize] ?? fmt.maxPlayers,
//       BR_PLATFORM_MAX_PLAYERS)
export const BR_PLATFORM_MAX_PLAYERS = 64;

export const GAME_PROFILES = {
    // ----- Fighting Games ----- //
    'sf6': {
        displayName: 'Street Fighter 6',
        abbr: 'SF6',
        type: 'fighting',
        allowedModes: ['bracket', 'h2h', 'wgn', 'ladder'],
        allowedPlatforms: ['PC', 'PS5', 'Xbox Series X|S', 'Cross-Platform'],
        formats: {
            'Best of 3': {
                description: 'A standard competitive set, first to 2 game wins.',
                durationMinutes: 10,
                teamSizes: [1],
                defaultTeamSize: 1,
            },
            'Best of 5': {
                description: 'A longer set for finals or bigger events, first to 3 game wins.',
                    durationMinutes: 15,
                    teamSizes: [1],
                    defaultTeamSize: 1,
                },
        },
    },
    'tk8': {
        displayName: 'Tekken 8',
        abbr: 'TK8',
        type: 'fighting',
        allowedModes: ['bracket', 'h2h', 'wgn', 'ladder'],
        allowedPlatforms: ['PC', 'PS5', 'Xbox Series X|S', 'Cross-Platform'],
        formats: {
            'Best of 3': {
                description: 'A standard tournament set, first to 2 game wins.',
                durationMinutes: 10,
                teamSizes: [1],
                defaultTeamSize: 1,
            },
            'Best of 5': {
                description: 'A longer set for finals or bigger events, first to 3 game wins.',
                durationMinutes: 15,
                teamSizes: [1],
                defaultTeamSize: 1,
            },
        },
    },
    'mk1': {
        displayName: 'Mortal Kombat 1',
        abbr: 'MK1',
        type: 'fighting',
        allowedModes: ['bracket', 'h2h', 'wgn', 'ladder'],
        allowedPlatforms: ['PC', 'PS5', 'Xbox Series X|S', 'Nintendo Switch', 'Cross-Platform'],
        formats: {
            'Best of 3': {
            description: 'A standard tournament set, first to 2 game wins.',
            durationMinutes: 10,
            teamSizes: [1],
            defaultTeamSize: 1,
            },
            'Best of 5': {
            description: 'Pro competitive standard, first to 3 game wins.',
            durationMinutes: 15,
            teamSizes: [1],
            defaultTeamSize: 1,
            },
        },
    },
    'ssb': {
        displayName: 'Super Smash Bros. Ultimate',
        abbr: 'SSB',
        type: 'fighting',
        allowedModes: ['bracket', 'h2h', 'wgn', 'ladder'],
        allowedPlatforms: ['Nintendo Switch'],
        formats: {
            'Best of 3 (3 Stock)': {
            description: 'Standard 1v1 competitive set with 3 stocks.',
            durationMinutes: 15,
            teamSizes: [1],
            defaultTeamSize: 1,
            },
            'Best of 5 (3 Stock)': {
           description: 'A longer set for finals, first to 3 wins with 3 stocks.',
            durationMinutes: 25,
            teamSizes: [1],
            defaultTeamSize: 1,
            },
            'Doubles (Best of 3)': {
            description: 'Standard 2v2 competitive set with 3 stocks.',
            durationMinutes: 15,
            teamSizes: [2],
            defaultTeamSize: 2,
            },
        },
    },
    'mtf': {
        displayName: 'MARVEL Tokon: Fighting Souls',
        abbr: 'MTF',
        type: 'fighting',
        allowedModes: ['bracket', 'h2h', 'wgn', 'ladder'],
        allowedPlatforms: ['PC', 'PS5', 'Xbox Series X|S', 'Nintendo Switch', 'Cross-Platform'],
        formats: {
            'Best of 3': {
                description: 'Standard competitive set, first to 2 game wins.',
                durationMinutes: 10,
                teamSizes: [1],
                defaultTeamSize: 1,
            },
            'Best of 5': {
                description: 'Extended set for finals or big moments, first to 3 game wins.',
                durationMinutes: 15,
                teamSizes: [1],
                defaultTeamSize: 1,
            },
        },
    },
    '2xk': {
        displayName: '2XKO',
        abbr: '2XK',
        type: 'fighting',
        allowedModes: ['bracket', 'h2h', 'wgn', 'ladder'],
        allowedPlatforms: ['PC', 'PS5', 'Xbox Series X|S', 'Cross-Platform'],
        formats: {
            'Best of 3': {
                description: 'Standard FGC set, first to 2 game wins.',
                durationMinutes: 12,
                teamSizes: [1],
                defaultTeamSize: 1,
            },
            'Best of 5': {
                description: 'Extended set for finals or big moments, first to 3 game wins.',
                durationMinutes: 18,
                teamSizes: [1],
                defaultTeamSize: 1,
            },
        },
    },
    // --- Sports --- //
    'mdn': {
        displayName: 'Madden',
        abbr: 'MDN',
        type: 'sports',
        allowedModes: ['bracket', 'h2h', 'ladder'],
        allowedPlatforms: ['PC', 'PS5', 'Xbox Series X|S', 'Cross-Platform'],
        formats: {
            // Single format — the real match shape (quarter length, skill,
            // clock, style) lives in MADDEN_GAME_SETTINGS / ev.gameSettings.
            'Standard': {
            description: 'Full game with the settings configured by the event creator.',
            durationMinutes: 35,
            teamSizes: [1],
            defaultTeamSize: 1,
            },
            // Legacy presets kept so events created before the game-settings
            // panel still resolve format metadata. Hidden from the create UI.
            '5-Min Quarters': {
            description: 'Standard competitive format with 5-minute quarters.',
            durationMinutes: 35,
            teamSizes: [1],
            defaultTeamSize: 1,
            legacy: true,
            },
            '3-Min Quarters': {
            description: 'A faster-paced match with 3-minute quarters.',
            durationMinutes: 25,
            teamSizes: [1],
            defaultTeamSize: 1,
            legacy: true,
            },
        },
    },
    'n2k': {
        displayName: 'NBA 2K',
        abbr: 'N2K',
        type: 'sports',
        allowedModes: ['bracket', 'h2h', 'ladder'],
        allowedPlatforms: ['PC', 'PS5', 'Xbox Series X|S', 'Nintendo Switch', 'Cross-Platform'],
        formats: {
            '5-Min Quarters': {
            description: 'Play a full 1v1 game with 5-minute quarters.',
            durationMinutes: 30,
            teamSizes: [1],
            defaultTeamSize: 1,
            },
            'Park (to 21)': {
            description: 'A quick park-style game to 21, win by 2.',
            durationMinutes: 15,
            teamSizes: [1, 2, 3], 
            defaultTeamSize: 3,
            },
        },
    },
    'rkl': {
        displayName: 'Rocket League',
        abbr: 'RKL',
        type: 'sports',
        allowedModes: ['bracket', 'h2h', 'ladder'],
        allowedPlatforms: ['PC', 'PS5', 'Xbox Series X|S', 'Nintendo Switch', 'Cross-Platform'],
        formats: {
            'Standard (3v3)': {
                description: 'Core competitive mode. First to outscore in 5-minute regulation (OT if tied).',
                durationMinutes: 10,
                teamSizes: [3],
                defaultTeamSize: 3,
            },
            'Doubles (2v2)': {
                description: 'Smaller teams, more space control and rotations.',
                durationMinutes: 8,
                teamSizes: [2],
                defaultTeamSize: 2,
            },
            'Duel (1v1)': {
                description: 'Pure mechanics and boost control in 1v1.',
                durationMinutes: 6,
                teamSizes: [1],
                defaultTeamSize: 1,
            },
        },
    },
    // --- shooting games --- //
    'rb6': {
        displayName: 'Rainbow Six Siege',
        abbr: 'R6S',
        type: 'shooter',
        allowedModes: ['bracket', 'h2h', 'ladder'],
        allowedPlatforms: ['PC', 'PS5', 'Xbox Series X|S', 'Cross-Platform'],
        maps: [
            'Oregon',
            'Clubhouse',
            'Kafe Dostoyevsky',
            'Consulate',
            'Bank',
            'Chalet',
            'Skyscraper',
            'Theme Park',
            'Nighthaven Labs',
        ],
        formats: {
            'Standard': {
            description: 'Standard 5v5 competitive rules with Attack/Defend rounds.',
            durationMinutes: 40,
            teamSizes: [5],
            defaultTeamSize: 5,
            },
            'Quick Match': {
            description: 'A faster-paced match with fewer rounds and a quicker prep phase.',
            durationMinutes: 25,
            teamSizes: [5],
            defaultTeamSize: 5,
            },
        },
    },
    'cod': {
        displayName: 'Call of Duty',
        abbr: 'COD',
        type: 'shooter',
        // Three event types: bracket = CDL team tournaments (host required at
        // 3v3+), h2h = 1v1 Head-to-Head single match (no host), showdown =
        // single-lobby battle-royale FFA events (Warzone playlists below;
        // dedicated non-playing host ALWAYS required). Runs under the
        // tournaments collection. Ladder is out of scope for COD.
        allowedModes: ['bracket', 'h2h', 'showdown'],
        allowedPlatforms: ['PC', 'PS5', 'Xbox Series X|S', 'Cross-Platform'],
        // COD is CDL-ruleset only. A non-playing host (CODCaster) is REQUIRED
        // for team events at/above hostMinTeamSize (3v3+); 1v1 and 2v2 need no
        // host. supportsHost reuses the GT7 dedicated-host path (host creates,
        // doesn't play or pay). See cod-event-config memory.
        supportsHost: true,
        requiresHost: true,
        hostMinTeamSize: 3,
        maps: [
            'Raid',
            'Standoff',
            'Firing Range',
            'Highrise',
            'Terminal',
            'Scrapyard',
            'Hardhat',
        ],
        // Order matters: CreateLeagueScreen defaults from the FIRST format, so
        // CDL Hardpoint must stay first (CDL modes, then Warzone BR playlists).
        // CDL team sizes 6v6→1v1 on every mode (default 6v6 —
        // canonical per the vision scoreboard reader). Host required at 3v3+.
        formats: {
            'CDL Hardpoint': {
            description: 'CDL Hardpoint — control the rotating objective zone to score time.',
            durationMinutes: 15,
            teamSizes: [6, 5, 4, 3, 2],
            defaultTeamSize: 6,
            },
            'CDL Overload': {
            description: 'CDL Overload — secure and bank the objective to score.',
            durationMinutes: 15,
            teamSizes: [6, 5, 4, 3, 2],
            defaultTeamSize: 6,
            },
            'CDL Search and Destroy': {
            description: 'CDL Search and Destroy — round-based, no respawns, plant or defuse.',
            durationMinutes: 25,
            teamSizes: [6, 5, 4, 3, 2],
            defaultTeamSize: 6,
            },
            'Gunfight': {
            description: 'Gunfight - small-map duels. Best for 1v1 and 2v2 - no host needed, winner reports.',
            durationMinutes: 10,
            teamSizes: [2, 1],
            defaultTeamSize: 2,
            },
            // --- Warzone battle-royale playlists (mode: 'showdown') --- //
            // BR events run as single-lobby FFA showdowns: one private match,
            // entries are per-player (event teamSize stays 1). teamSizes below
            // are the allowed IN-GAME SQUAD sizes (1=Solos, 2=Duos, 3=Trios,
            // 4=Quads); defaultTeamSize is the default squad size. New fields:
            //   battleRoyale: true -- the discriminator flag for all BR branches
            //   maxPlayers -- private-lobby player cap (the format's best-case
            //     cap, used for display + fallback). The CODCaster host
            //     spectates and NEVER consumes a player slot (players + host).
            //   maxPlayersBySquadSize -- optional { squadSize: cap } map that
            //     overrides maxPlayers for that squad size (some playlists
            //     hand-tune the lobby cap per squad size).
            //   minPlayers -- the GAME's floor to start a private match; events
            //     under this at lock auto-cancel and refund every entry.
            // A dedicated non-playing host is ALWAYS required for non-league BR
            // events ("no host, no event"), at every squad size. Caps/floors
            // retuned from the founder's in-lobby sweep 2026-07-20: the red
            // "/N PLAYERS" numbers in-game are FLOORS (minimum to launch); the
            // squads view shows the true caps. Effective event cap =
            //   min(maxPlayersBySquadSize?.[squadSize] ?? maxPlayers,
            //       BR_PLATFORM_MAX_PLAYERS)
            // -- keep caps/floors as data here, never scatter literals.
            'Warzone BR: Verdansk': {
            description: 'Warzone battle royale on the full Verdansk map - outlast the lobby.',
            durationMinutes: 30,
            teamSizes: [1, 2, 3, 4],
            defaultTeamSize: 4,
            battleRoyale: true,
            // cap verified in-lobby 2026-07-20 (75 duo squads); platform clamps
            // to BR_PLATFORM_MAX_PLAYERS until scoring automates.
            maxPlayers: 150,
            minPlayers: 50,
            },
            'Warzone BR: Avalon': {
            description: 'Warzone battle royale on the Avalon map - outlast the lobby.',
            durationMinutes: 30,
            teamSizes: [1, 2, 3, 4],
            defaultTeamSize: 4,
            battleRoyale: true,
            // floor verified in-lobby 2026-07-20; cap 150 INFERRED from
            // BR-family parity with Verdansk - not yet verified in-lobby.
            maxPlayers: 150,
            minPlayers: 50,
            },
            'Black Ops Royale: Avalon': {
            description: 'Black Ops Royale on Avalon - the Black Ops 7 battle royale ruleset.',
            durationMinutes: 25,
            teamSizes: [1, 4],
            defaultTeamSize: 4,
            battleRoyale: true,
            // floor+cap unverified in-lobby - retune when founder checks BOR.
            maxPlayers: 50,
            minPlayers: 50,
            },
            'Resurgence: Rebirth Island': {
            description: 'Resurgence on Rebirth Island - fast respawns while a squadmate survives. Lobby cap depends on squad size.',
            durationMinutes: 15,
            teamSizes: [1, 2, 3, 4],
            defaultTeamSize: 3,
            battleRoyale: true,
            // Verified in-lobby 2026-07-20 (non-ranked privates): Duos 25x2=50,
            // Trios 17x3=51 (trios EXISTS here), Quads 11x4=44; floor 35. Solos
            // cap 50 is ASSUMED, not verified. Default squad size is trios:
            // biggest lobby (51) and best pot.
            maxPlayers: 51,
            maxPlayersBySquadSize: { 1: 50, 2: 50, 3: 51, 4: 44 },
            minPlayers: 35,
            },
            'Resurgence: Haven\'s Hollow': {
            description: 'Resurgence on Haven\'s Hollow - fast respawns while a squadmate survives. Lobby cap depends on squad size.',
            durationMinutes: 15,
            teamSizes: [1, 2, 3, 4],
            defaultTeamSize: 3,
            battleRoyale: true,
            // Verified in-lobby 2026-07-20 (non-ranked privates): Duos 25x2=50,
            // Trios 17x3=51 (trios EXISTS here), Quads 11x4=44; floor 35. Solos
            // cap 50 is ASSUMED, not verified. Default squad size is trios:
            // biggest lobby (51) and best pot.
            maxPlayers: 51,
            maxPlayersBySquadSize: { 1: 50, 2: 50, 3: 51, 4: 44 },
            minPlayers: 35,
            },
            'Resurgence: Fortune\'s Keep': {
            description: 'Resurgence on Fortune\'s Keep - fast respawns while a squadmate survives. Lobby cap depends on squad size.',
            durationMinutes: 15,
            teamSizes: [1, 2, 3, 4],
            defaultTeamSize: 3,
            battleRoyale: true,
            // Verified in-lobby 2026-07-20 (non-ranked privates): Duos 25x2=50,
            // Trios 17x3=51 (trios EXISTS here), Quads 11x4=44; floor 35. Solos
            // cap 50 is ASSUMED, not verified. Default squad size is trios:
            // biggest lobby (51) and best pot.
            maxPlayers: 51,
            maxPlayersBySquadSize: { 1: 50, 2: 50, 3: 51, 4: 44 },
            minPlayers: 35,
            },
            'Ranked Play: Rebirth Island': {
            description: 'Ranked Play Resurgence trios on Rebirth Island - competitive ruleset.',
            durationMinutes: 20,
            teamSizes: [3],
            defaultTeamSize: 3,
            battleRoyale: true,
            // cap verified in-lobby 2026-07-20 (16 trios = 48); floor 35 is
            // unverified for ranked privates.
            maxPlayers: 48,
            minPlayers: 35,
            },
            'Ranked Play: Fortune\'s Keep': {
            description: 'Ranked Play Resurgence trios on Fortune\'s Keep - competitive ruleset.',
            durationMinutes: 20,
            teamSizes: [3],
            defaultTeamSize: 3,
            battleRoyale: true,
            // cap verified in-lobby 2026-07-20 (16 trios = 48); floor 35 is
            // unverified for ranked privates.
            maxPlayers: 48,
            minPlayers: 35,
            },
            'Ranked Play: Haven\'s Hollow': {
            description: 'Ranked Play Resurgence trios on Haven\'s Hollow - competitive ruleset.',
            durationMinutes: 20,
            teamSizes: [3],
            defaultTeamSize: 3,
            battleRoyale: true,
            // cap verified in-lobby 2026-07-20 (16 trios = 48); floor 35 is
            // unverified for ranked privates.
            maxPlayers: 48,
            minPlayers: 35,
            },
        },
    },
    'mvr': {
        displayName: 'Marvel Rivals',
        abbr: 'MVR',
        type: 'shooter',
        allowedModes: ['bracket', 'h2h', 'ladder'],
        allowedPlatforms: ['PC', 'PS5', 'Xbox Series X|S', 'Cross-Platform'],
        // Non-league 6v6 team events REQUIRE a non-playing host (founder
        // 2026-07-15): no host, no event. Mirrors functions/constants/gameData.js.
        supportsHost: true,
        requiresHost: true,
        hostMinTeamSize: 3,

        roomSettings: {
            roomTypes: [
                { key: 'casual', label: 'Casual Room', description: 'A standard room for casual play.' },
                { key: 'tournament', label: 'Tournament Room', description: 'A room with advanced competitive settings.' },
            ],
            common: {
                joinAccess: {
                    label: 'Join Access',
                    options: [
                        { key: 'open', label: 'Open to All' },
                        { key: 'friends', label: 'Friends Only' },
                        { key: 'invite', label: 'Invitation Only' },
                    ],
                    default: 'open',
                },
                joinDuringBattle: {
                    label: 'Join During Battle',
                    options: [
                        { key: 'allow', label: 'Allow' },
                        { key: 'apply', label: 'Apply to Join' },
                        { key: 'deny', label: 'Deny' },
                    ],
                default: 'deny',
                },
                spectatorAccess: {
                    label: 'Spectator Access',
                    options: [
                        { key: 'open', label: 'Open to All' },
                        { key: 'friends', label: 'Friends Only' },
                        { key: 'none', label: 'No Spectating' },
                    ],
                    default: 'friends',
                },
            },
            tournament: {
                banPick: {
                    label: 'Ban/Pick Settings',
                    options: [
                        { key: 'none', label: 'No Ban/Pick' },
                        { key: 'competitive', label: 'Competitive' },
                        { key: 'tournament', label: 'Tournament Mode' },
                        { key: 'advanced', label: 'Tournament (Advanced)' },
                    ],
                    default: 'competitive',
                },
                bestOf: {
                    label: 'Format (Best of)',
                    min: 1,
                    max: 9,
                    default: 3,
                },
                pausePermissions: {
                    label: 'Pause Permissions',
                    options: [
                        { key: 'host', label: 'Host Only' },
                        { key: 'coach_players', label: 'Coach & Players' },
                    ],
                    default: 'host',
                },
                movePermissions: {
                    label: 'Move Permissions',
                    options: [
                        { key: 'host', label: 'Host Only' },
                        { key: 'players', label: 'Players Can Move' },
                    ],
                    default: 'host',
                },
                balanceLatency: {
                    label: 'Balance Latency',
                    default: true, 
                },
                autoServerSelect: {
                    label: 'Auto-Select Server',
                    default: true, 
                },
            }
        },
        formats: {
            'Convoy': {
                description: 'Escort the payload to the destination while fighting off the enemy team.',
                durationMinutes: 15,
                teamSizes: [6],
                defaultTeamSize: 6,
                maps: [
                    'Yggsgard, Yggdrasill Path',
                    'Tokyo 2099, Spider-Islands',
                    'Empire of Eternal Night, Midtown',
                    'Hellfire Gala, Arakko',
                ],
            },
            'Domination': {
                description: 'Capture and hold a central objective point to score.',
                durationMinutes: 15,
                teamSizes: [6],
                defaultTeamSize: 6,
                maps: [
                    'Yggsgard, Royal Palace',
                    'Intergalactic Empire of Wakanda, Birnin T\'Challa',
                    'Hydra Charteris Base, Hell\'s Heaven',
                    'Hellfire Gala, Krakoa',
                    'Klyntar, Celestial Husk',
                ],
            },
            'Convergence': {
                description: 'The core 6v6 mode. Battle for control of a shifting cosmic cube.',
                durationMinutes: 20,
                teamSizes: [6],
                defaultTeamSize: 6,
                maps: [
                    'Tokyo 2099, Shin-Shibuya',
                    'Intergalactic Empire of Wakanda, Hall of Djalia',
                    'Klyntar, Symbiotic Surface',
                    'Empire of Eternal Night, Central Park',
                ],
            },
        },
    },
    'val': {
        displayName: 'Valorant',
        abbr: 'VAL',
        type: 'shooter',
        allowedModes: ['bracket', 'h2h', 'ladder'],
        allowedPlatforms: ['PC'],
        maps: [
            'Ascent', 'Bind', 'Breeze', 'Haven', 'Lotus', 'Split', 'Sunset'
        ],
        roomSettings: {
            roomTypes: [
            { key: 'standard', label: 'Standard Game', description: 'A regular custom game.' },
            { key: 'tournament', label: 'Tournament Mode', description: 'Enables competitive tournament features.' },
            ],
            common: {
                allowCheats: {
                label: 'Allow Cheats',
                default: false, 
                description: 'Enables commands for practice or custom scenarios.'
                },
                spectatorAccess: {
                    label: 'Spectator Access',
                    options: [
                        { key: 'allow', label: 'Allow Spectators' },
                        { key: 'deny', label: 'No Spectators' },
                        ],
                    default: 'allow',
                },
            },
            tournament: {
                overtimeRules: {
                    label: 'Overtime Rules',
                    options: [
                        { key: 'win_by_two', label: 'Win by Two' },
                        { key: 'vote_draw', label: 'Vote to Draw' },
                    ],
                    default: 'win_by_two',
                    description: 'Determines how to resolve a 12-12 tie.'
                },
                tacticalTimeouts: {
                    label: 'Tactical Timeouts',
                    options: [
                        { key: 'none', label: 'None' },
                        { key: 'one_per_half', label: '1 Per Half' },
                        { key: 'two_per_half', label: '2 Per Half' },
                    ],
                    default: 'two_per_half',
                    description: 'Allows teams to call a tactical pause during the game.'
                },
            }
        },
        formats: {
            'Competitive': {
                description: 'Standard 5v5 competitive rules, first to 13 rounds.',
                durationMinutes: 45,
                teamSizes: [5],
                defaultTeamSize: 5,
            },
            'Swiftplay': {
                description: 'A shorter, first-to-5-rounds format for faster games.',
                durationMinutes: 20,
                teamSizes: [5],
                defaultTeamSize: 5,
            },
        },
    },
    'cs2': {
        displayName: 'Counter-Strike 2',
        abbr: 'CS2',
        type: 'shooter',
        allowedModes: ['bracket', 'h2h', 'ladder'],
        allowedPlatforms: ['PC'],
        maps: [
            'Inferno',
            'Mirage',
            'Nuke',
            'Overpass',
            'Vertigo',
            'Ancient',
            'Anubis',
        ],
  
        roomSettings: {
            roomTypes: [
                { key: 'standard', label: 'Standard Game', description: 'A regular custom match.' },
                { key: 'tournament', label: 'Tournament Mode', description: 'Enables competitive tournament features.' },
            ],
            common: {
                sv_cheats: {
                    label: 'Enable Cheats',
                    default: false, 
                    description: 'Allows server commands like noclip, god mode, etc.'
                },
            spectatorAccess: {
                label: 'Spectator Access',
                options: [
                    { key: 'allow', label: 'Allow Spectators' },
                    { key: 'deny', label: 'No Spectators' },
                ],
                default: 'allow',
            },
            },
            tournament: {
                overtime: {
                    label: 'Overtime',
                    options: [
                        { key: 'enabled', label: 'Enable Overtime' },
                        { key: 'disabled', label: 'End on a Draw' },
                    ],
                    default: 'enabled',
                    description: 'Determines if the game continues after a 12-12 tie.'
                },
                pausePermissions: {
                    label: 'Pause Match',
                    options: [
                        { key: 'allow', label: 'Allow Pauses' },
                        { key: 'deny', label: 'No Pauses' },
                    ],
                    default: 'allow',
                    description: 'Allows teams to call for a technical or tactical pause.'
                },
            }
        },
        formats: {
            'Competitive (MR12)': {
                description: 'Standard 5v5 competitive match, first to 13 rounds.',
                durationMinutes: 45,
                teamSizes: [5],
                defaultTeamSize: 5,
            },
            'Short Match (MR8)': {
                description: 'A faster competitive match, first to 9 rounds.',
                durationMinutes: 25,
                teamSizes: [5],
                defaultTeamSize: 5,
            },
            'Wingman (2v2)': {
                description: 'Fast-paced 2v2 competitive mode on a smaller map.',
                durationMinutes: 15,
                teamSizes: [2],
                defaultTeamSize: 2,
            },
        },
    },
    'apl': {
    displayName: 'Apex Legends',
    abbr: 'APL',
    type: 'shooter',
    allowedModes: ['bracket', 'h2h', 'ladder'],
    allowedPlatforms: ['PC', 'PS5', 'PS4', 'Xbox Series X|S', 'Xbox One', 'Nintendo Switch', 'Cross-Platform'],
    maps: [
        'Kings Canyon',
        'World\'s Edge',
        'Olympus',
        'Storm Point',
        'Broken Moon',
    ],
    roomSettings: {
        roomTypes: [
        { key: 'standard', label: 'Standard Match', description: 'A regular custom game.' },
        { key: 'competitive', label: 'Competitive Scrim', description: 'Enables common competitive settings.' },
        ],
        common: {
        teamSize: {
            label: 'Team Size',
            options: [
            { key: 'duos', label: 'Duos (Teams of 2)' },
            { key: 'trios', label: 'Trios (Teams of 3)' },
            ],
            default: 'trios',
        },
        spectatorAccess: {
            label: 'Spectator Access',
            options: [
            { key: 'allow', label: 'Allow' },
            { key: 'deny', label: 'Deny' },
            ],
            default: 'allow',
        },
        },
        competitive: {
        startingShields: {
            label: 'Starting Shields',
            options: [
            { key: 'white', label: 'White Evo' },
            { key: 'blue', label: 'Blue Evo' },
            ],
            default: 'white',
            description: 'Determines the armor players start with.'
        },
        allowEvacTowers: {
            label: 'Enable Evac Towers',
            default: true, 
            description: 'Allow the use of mobile redeploy balloons.'
        },
        allowHeatShields: {
            label: 'Enable Heat Shields',
            default: true, 
            description: 'Allow the use of heat shields in the ring.'
        }
        }
    },
    formats: {
        'Battle Royale': {
        description: 'Standard Battle Royale format. Last squad standing wins.',
        durationMinutes: 25,
        teamSizes: [2, 3],
        defaultTeamSize: 3,
        },
        'Mixtape': {
        description: 'A rotation of faster modes like TDM or Control.',
        durationMinutes: 15,
        teamSizes: [3, 6], 
        defaultTeamSize: 3,
        },
    },
    },
    'ow2': {
    displayName: 'Overwatch 2',
    abbr: 'OW2',
    type: 'shooter',
    allowedModes: ['bracket', 'h2h', 'ladder'],
    allowedPlatforms: ['PC', 'PS5', 'Xbox Series X|S', 'Nintendo Switch', 'Cross-Platform'],
    maps: {
        Control: ['Lijiang Tower', 'Ilios', 'Oasis', 'Nepal', 'Antarctic Peninsula'],
        Escort: ['Dorado', 'Route 66', 'Junkertown', 'Circuit Royal', 'Havana'],
        Hybrid: ['King\'s Row', 'Numbani', 'Eichenwalde', 'Hollywood', 'Blizzard World'],
        Push: ['New Queen Street', 'Colosseo', 'EsperanÃ§a'],
        Clash: ['Hanaoka'],
    },
    
    roomSettings: {
        roomTypes: [
        { key: 'standard', label: 'Standard Game', description: 'A regular custom game.' },
        { key: 'competitive', label: 'Competitive Workshop', description: 'Enables common competitive settings.' },
        ],
        common: {
        spectatorAccess: {
            label: 'Spectator Slots',
            options: [
            { key: 'allow', label: 'Allow' },
            { key: 'deny', label: 'Deny' },
            ],
            default: 'allow',
        },
        },
        competitive: {
        mapControl: {
            label: 'Map Control',
            options: [
            { key: 'random_map', label: 'Random Map' },
            { key: 'map_pool', label: 'Use Map Pool' },
            ],
            default: 'map_pool',
            description: 'Choose between a single random map or a pool for the event.'
        },
        heroSettings: {
            label: 'Hero Settings',
            options: [
            { key: 'no_bans', label: 'No Hero Bans' },
            { key: 'one_ban', label: '1 Hero Ban Per Team' },
            { key: 'two_bans', label: '2 Hero Bans Per Team' },
            ],
            default: 'no_bans',
            description: 'Set the number of heroes each team can ban before the match.'
        },
        gameplaySettings: {
            label: 'Gameplay Settings',
            options: [
            { key: 'kill_cam_on', label: 'Enable Kill Cam' },
            { key: 'kill_cam_off', label: 'Disable Kill Cam' },
            ],
            default: 'kill_cam_off',
        }
        }
    },
    formats: {
        'Role Queue (5v5)': {
        description: 'Standard competitive format with a 1 Tank, 2 Damage, 2 Support role lock.',
        durationMinutes: 20,
        teamSizes: [5],
        defaultTeamSize: 5,
        },
        'Open Queue (5v5)': {
        description: 'Players can choose any role; no team composition restrictions.',
        durationMinutes: 20,
        teamSizes: [5],
        defaultTeamSize: 5,
        },
        'Mystery Heroes': {
        description: 'A casual mode where your hero is randomly selected each time you spawn.',
        durationMinutes: 15,
        teamSizes: [5],
        defaultTeamSize: 5,
        }
    },
    },
    // --- MOBAS --- //
    'dt2': {
    displayName: 'Dota 2',
    abbr: 'DT2',
    type: 'moba',
    allowedModes: ['bracket', 'h2h', 'ladder'],
    allowedPlatforms: ['PC'],
    
    roomSettings: {
        roomTypes: [
        { key: 'standard', label: 'Standard Lobby', description: 'A regular custom lobby.' },
        { key: 'tournament', label: 'Tournament Lobby', description: 'Enables competitive draft modes.' },
        ],
        common: {
        spectatorAccess: {
            label: 'Spectator Access',
            options: [
            { key: 'allow', label: 'Allow' },
            { key: 'deny', label: 'Deny' },
            ],
            default: 'allow',
        },
        },
        tournament: {
        draftMode: {
            label: 'Draft Mode',
            options: [
            { key: 'captains_mode', label: 'Captain\'s Mode' },
            { key: 'all_pick', label: 'All Pick with Bans' },
            ],
            default: 'captains_mode',
            description: 'Determines how heroes are picked and banned.'
        },
        pausePermissions: {
            label: 'Pause Match',
            options: [
            { key: 'unlimited', label: 'Unlimited' },
            { key: 'limited', label: 'Limited Pauses' },
            ],
            default: 'limited',
            description: 'Set rules for tactical and technical pauses.'
        },
        }
    },
    formats: {
        'All Pick': {
        description: 'The standard 5v5 game mode for Dota 2.',
        durationMinutes: 45,
        teamSizes: [5],
        defaultTeamSize: 5,
        },
        'Turbo': {
        description: 'A much faster-paced version of the standard game.',
        durationMinutes: 25,
        teamSizes: [5],
        defaultTeamSize: 5,
        },
    },
    },
    'lol': {
    displayName: 'League of Legends',
    abbr: 'LOL',
    type: 'moba',
    allowedModes: ['bracket', 'h2h', 'ladder'],
    allowedPlatforms: ['PC'],
    
    roomSettings: {
        roomTypes: [
        { key: 'standard', label: 'Standard Lobby', description: 'A regular custom lobby.' },
        { key: 'tournament', label: 'Tournament Lobby', description: 'Enables competitive draft features.' },
        ],
        common: {
        spectatorAccess: {
            label: 'Spectator Access',
            options: [
            { key: 'allow', label: 'Allow' },
            { key: 'deny', label: 'Deny' },
            ],
            default: 'allow',
        },
        },
        tournament: {
        draftMode: {
            label: 'Draft Mode',
            options: [
            { key: 'tournament_draft', label: 'Tournament Draft' },
            { key: 'draft_pick', label: 'Draft Pick' },
            { key: 'blind_pick', label: 'Blind Pick' },
            ],
            default: 'tournament_draft',
            description: 'Determines how champions are picked and banned.'
        },
        pausePermissions: {
            label: 'Pause Match',
            options: [
            { key: 'allow', label: 'Allow Pauses' },
            { key: 'deny', label: 'No Pauses' },
            ],
            default: 'allow',
            description: 'Allows for tactical or technical pauses during the game.'
        },
        }
    },
    formats: {
        'Summoner\'s Rift (5v5)': {
        description: 'The classic 5v5 MOBA experience on the main map.',
        durationMinutes: 40,
        teamSizes: [5],
        defaultTeamSize: 5,
        },
        'ARAM (5v5)': {
        description: 'A fast-paced, all-random, all-mid 5v5 mode on the Howling Abyss.',
        durationMinutes: 20,
        teamSizes: [5],
        defaultTeamSize: 5,
        },
    },
    },
    // --- Racing Games --- //
    'irc': {
        displayName: 'iRacing',
        abbr: 'iRC',
        type: 'racing',
        allowedModes: ['racing', 'h2h', 'ladder'],
        allowedPlatforms: ['PC'],
        maxPlayers: 60, // iRacing supports large grids
        tracks: [
            'Daytona International Speedway',
            'Watkins Glen International',
            'Spa-Francorchamps',
            'NÃ¼rburgring Nordschleife',
            'Suzuka Circuit',
            'Silverstone Circuit',
            'Road America',
            'Sebring International Raceway',
            'Indianapolis Motor Speedway',
            'Laguna Seca',
            'Monza',
            'Brands Hatch',
            'Imola',
            'Mount Panorama (Bathurst)',
            'Circuit of the Americas',
            'Road Atlanta',
            'VIR (Virginia International Raceway)',
            'Lime Rock Park',
            'Mid-Ohio Sports Car Course',
            'Oulton Park',
        ],

        roomSettings: {
            roomTypes: [
                { key: 'hosted', label: 'Hosted Session', description: 'A private hosted race session.' },
                { key: 'league', label: 'League Race', description: 'Official league competition format.' },
            ],
            common: {
                carClass: {
                    label: 'Car Class',
                    options: [
                        { key: 'gt3', label: 'GT3' },
                        { key: 'gt4', label: 'GT4' },
                        { key: 'lmp2', label: 'LMP2' },
                        { key: 'gtp', label: 'GTP (Hypercar)' },
                        { key: 'formula', label: 'Formula' },
                        { key: 'stock', label: 'Stock Cars (NASCAR)' },
                        { key: 'mx5', label: 'Mazda MX-5' },
                        { key: 'tcr', label: 'TCR' },
                    ],
                    default: 'gt3',
                    description: 'The car category for the event.'
                },
                spectatorAccess: {
                    label: 'Spectator Access',
                    options: [
                        { key: 'allow', label: 'Allow' },
                        { key: 'deny', label: 'Deny' },
                    ],
                    default: 'allow',
                },
            },
            league: {
                incidentLimit: {
                    label: 'Incident Limit',
                    options: [
                        { key: 'none', label: 'No Limit' },
                        { key: '17x', label: '17x (Standard)' },
                        { key: '25x', label: '25x (Lenient)' },
                    ],
                    default: '17x',
                    description: 'Maximum incident points before disqualification.'
                },
                fixedSetup: {
                    label: 'Setup Type',
                    options: [
                        { key: 'fixed', label: 'Fixed Setup' },
                        { key: 'open', label: 'Open Setup' },
                    ],
                    default: 'open',
                    description: 'Whether drivers can modify car setups.'
                },
                fastRepairs: {
                    label: 'Fast Repairs',
                    options: [
                        { key: '0', label: 'None' },
                        { key: '1', label: '1 Fast Repair' },
                        { key: '2', label: '2 Fast Repairs' },
                    ],
                    default: '0',
                    description: 'Number of instant repairs allowed.'
                },
            }
        },

        formats: {
            'Sprint Race': {
                description: 'A short race, typically 15-20 minutes.',
                durationMinutes: 20,
                teamSizes: [1],
                defaultTeamSize: 1,
            },
            'Feature Race': {
                description: 'A standard race length, 30-45 minutes.',
                durationMinutes: 40,
                teamSizes: [1],
                defaultTeamSize: 1,
            },
            'Endurance (1 Hour)': {
                description: 'An endurance event testing consistency.',
                durationMinutes: 65,
                teamSizes: [1, 2],
                defaultTeamSize: 1,
            },
            'Team Endurance': {
                description: 'Multi-driver endurance event with driver swaps.',
                durationMinutes: 120,
                teamSizes: [2, 3, 4],
                defaultTeamSize: 2,
            },
        },
    },
    'gt7': {
    displayName: 'Gran Turismo 7',
    abbr: 'GT7',
    type: 'racing',
    supportsHost: true,  // 15 racers + 1 host (16-player lobby)
    allowedModes: ['racing', 'h2h', 'ladder'],
    allowedPlatforms: ['PS5', 'PS4'],
    maxPlayers: 15,  // 15 racers + 1 host (16-player lobby)
    tracks: [
        { name: '24 Heures du Mans race track, no chicane', lengthMiles: 8.43, type: 'road' },
        { name: 'Alsace Test Course', lengthMiles: 1.30, type: 'rally' },
        { name: 'Alsace Village', lengthMiles: 3.36, type: 'rally' },
        { name: 'Autodromo de Interlagos', lengthMiles: 2.68, type: 'road' },
        { name: 'Autodromo Nazionale Monza', lengthMiles: 3.6, type: 'road' },
        { name: 'Autodromo Nazionale Monza No Chicane', lengthMiles: 3.58, type: 'road' },
        { name: 'Autopolis International Racing Course', lengthMiles: 2.9, type: 'road' },
        { name: 'Autopolis International Racing Course - Short Course', lengthMiles: 1.88, type: 'road' },
        { name: 'Blue Moon Bay Speedway', lengthMiles: 1.99, type: 'oval' },
        { name: 'Blue Moon Bay Speedway Infield A', lengthMiles: 2.11, type: 'road' },
        { name: 'Blue Moon Bay Speedway Infield B', lengthMiles: 1.80, type: 'road' },
        { name: 'Brands Hatch Grand Prix Circuit', lengthMiles: 2.45, type: 'road' },
        { name: 'Brands Hatch Indy Circuit', lengthMiles: 1.21, type: 'road' },
        { name: 'Broad Bean Raceway', lengthMiles: 1.06, type: 'road' },
        { name: 'Circuit de Barcelona-Catalunya GP Layout', lengthMiles: 2.89, type: 'road' },
        { name: 'Circuit de Barcelona-Catalunya GP Layout No Chicane', lengthMiles: 2.94, type: 'road' },
        { name: 'Circuit de Barcelona-Catalunya National Layout', lengthMiles: 1.85, type: 'road' },
        { name: 'Circuit de Barcelona-Catalunya Rallycross', lengthMiles: 0.68, type: 'rally' },
        { name: 'Circuit de Spa-Francorchamps', lengthMiles: 4.35, type: 'road' },
        { name: 'Circuit de Spa-Francorchamps 24h layout', lengthMiles: 4.35, type: 'road' },
        { name: 'Circuit Gilles-Villeneuve', lengthMiles: 2.71, type: 'road' },
        { name: 'Colorado Springs Lake', lengthMiles: 1.86, type: 'rally' },
        { name: 'Daytona Road Course', lengthMiles: 3.56, type: 'road' },
        { name: 'Daytona Tri-Oval', lengthMiles: 2.5, type: 'oval' },
        { name: 'Deep Forest Raceway', lengthMiles: 2.67, type: 'road' },
        { name: 'Dragon Trail Gardens', lengthMiles: 2.73, type: 'road' },
        { name: 'Dragon Trail Seaside', lengthMiles: 3.23, type: 'road' },
        { name: 'Eiger Nordwand', lengthMiles: 1.49, type: 'road' },
        { name: 'Fishermans Ranch', lengthMiles: 4.29, type: 'rally' },
        { name: 'Fuji International Speedway', lengthMiles: 2.84, type: 'road' },
        { name: 'Fuji International Speedway (Short)', lengthMiles: 2.81, type: 'road' },
        { name: 'Goodwood Motor Circuit', lengthMiles: 2.37, type: 'road' },
        { name: 'Grand Valley Highway 1', lengthMiles: 3.17, type: 'road' },
        { name: 'Grand Valley South', lengthMiles: 1.93, type: 'road' },
        { name: 'High Speed Ring', lengthMiles: 2.67, type: 'road' },
        { name: 'Kyoto Driving Park Miyabi', lengthMiles: 1.24, type: 'road' },
        { name: 'Kyoto Driving Park Yamagiwa', lengthMiles: 3.04, type: 'road' },
        { name: 'Kyoto Driving Park Yamagiwa + Miyabi', lengthMiles: 4.23, type: 'road' },
        { name: 'Lake Louise Long Track', lengthMiles: 2.30, type: 'rally' },
        { name: 'Lake Louise Short Track', lengthMiles: 1.62, type: 'rally' },
        { name: 'Lake Louise Tri-Oval', lengthMiles: 1.93, type: 'rally' },
        { name: 'Lake Maggiore Centre', lengthMiles: 1.06, type: 'road' },
        { name: 'Lake Maggiore East', lengthMiles: 2.24, type: 'road' },
        { name: 'Lake Maggiore East End', lengthMiles: 1.24, type: 'road' },
        { name: 'Lake Maggiore Full Course', lengthMiles: 3.60, type: 'road' },
        { name: 'Lake Maggiore West', lengthMiles: 2.61, type: 'road' },
        { name: 'Lake Maggiore West End', lengthMiles: 1.49, type: 'road' },
        { name: 'Michelin Raceway Road Atlanta', lengthMiles: 2.54, type: 'road' },
        { name: 'Mount Panorama Circuit', lengthMiles: 3.85, type: 'road' },
        { name: 'Northern Isle Speedway', lengthMiles: 0.56, type: 'oval' },
        { name: 'N\u00fcrburgring 24h', lengthMiles: 15.77, type: 'road' },
        { name: 'N\u00fcrburgring Endurance', lengthMiles: 14.83, type: 'road' },
        { name: 'N\u00fcrburgring GP', lengthMiles: 3.2, type: 'road' },
        { name: 'N\u00fcrburgring Nordschleife', lengthMiles: 12.94, type: 'road' },
        { name: 'N\u00fcrburgring Sprint', lengthMiles: 2.26, type: 'road' },
        { name: 'Red Bull Ring', lengthMiles: 2.68, type: 'road' },
        { name: 'Red Bull Ring Short Track', lengthMiles: 1.45, type: 'road' },
        { name: 'Sainte-Croix Layout A', lengthMiles: 5.90, type: 'rally' },
        { name: 'Sainte-Croix Layout B', lengthMiles: 4.41, type: 'rally' },
        { name: 'Sainte-Croix Layout C', lengthMiles: 6.71, type: 'rally' },
        { name: 'Sardegna Road Track Layout A', lengthMiles: 3.17, type: 'road' },
        { name: 'Sardegna Road Track Layout B', lengthMiles: 2.42, type: 'road' },
        { name: 'Sardegna Road Track Layout C', lengthMiles: 1.68, type: 'road' },
        { name: 'Sardegna Windmills', lengthMiles: 2.05, type: 'road' },
        { name: 'Special Stage Route X', lengthMiles: 18.83, type: 'oval' },
        { name: 'Suzuka Circuit', lengthMiles: 3.61, type: 'road' },
        { name: 'Suzuka Circuit East Course', lengthMiles: 1.39, type: 'road' },
        { name: 'Tokyo Expressway Central Clockwise', lengthMiles: 2.73, type: 'road' },
        { name: 'Tokyo Expressway Central Counterclockwise', lengthMiles: 2.73, type: 'road' },
        { name: 'Tokyo Expressway East Clockwise', lengthMiles: 4.54, type: 'road' },
        { name: 'Tokyo Expressway East Counterclockwise', lengthMiles: 4.47, type: 'road' },
        { name: 'Tokyo Expressway South Clockwise', lengthMiles: 3.23, type: 'road' },
        { name: 'Tokyo Expressway South Counterclockwise', lengthMiles: 4.10, type: 'road' },
        { name: 'Trial Mountain Circuit', lengthMiles: 3.36, type: 'road' },
        { name: 'Tsukuba Circuit', lengthMiles: 1.27, type: 'road' },
        { name: 'Watkins Glen Long Course', lengthMiles: 3.36, type: 'road' },
        { name: 'Watkins Glen Short Course', lengthMiles: 2.45, type: 'road' },
        { name: 'WeatherTech Raceway Laguna Seca', lengthMiles: 2.24, type: 'road' },
        { name: 'Willow Springs International Raceway: Big Willow', lengthMiles: 2.46, type: 'road' },
        { name: 'Willow Springs International Raceway: Horse Thief Mile', lengthMiles: 1.01, type: 'road' },
        { name: 'Willow Springs International Raceway: Horse Thief Mile Reverse', lengthMiles: 1.01, type: 'road' },
        { name: 'Willow Springs International Raceway: Streets of Willow Springs', lengthMiles: 1.66, type: 'road' },
        { name: 'Willow Springs International Raceway: Streets of Willow Springs Reverse', lengthMiles: 1.66, type: 'road' },
        { name: 'Yas Marina Circuit', lengthMiles: 3.28, type: 'road' },
    ],

    roomSettings: {
        common: {
        carCategory: {
            label: 'Vehicle Category',
            options: [
            { key: 'no_limit', label: 'No Limit' },
            { key: 'gr1', label: 'Gr.1' },
            { key: 'gr2', label: 'Gr.2' },
            { key: 'gr3', label: 'Gr.3' },
            { key: 'gr4', label: 'Gr.4' },
            { key: 'grB', label: 'Gr.B' },
            ],
            default: 'gr3',
            description: 'All racers must use vehicles from the selected category. No mixing allowed.'
        },
        },
    },

    formats: {
        '5 Laps': {
        description: 'A 5-lap race with no mandatory pit stops.',
        durationMinutes: 10,
        teamSizes: [1],
        defaultTeamSize: 1,
        laps: 5,
        minPitStops: 0,
        requiredTireTypeChange: false,
        },
        '10 Laps': {
        description: 'A 10-lap race with 1 mandatory pit stop and required tire type change.',
        durationMinutes: 20,
        teamSizes: [1],
        defaultTeamSize: 1,
        laps: 10,
        minPitStops: 1,
        requiredTireTypeChange: true,
        },
        '20 Laps': {
        description: 'A 20-lap endurance race with 2 mandatory pit stops and required tire type changes.',
        durationMinutes: 40,
        teamSizes: [1],
        defaultTeamSize: 1,
        laps: 20,
        minPitStops: 2,
        requiredTireTypeChange: true,
        },
    },
    },
    // ============================================================================
    // CRITCASH ARENA - FIRST PARTY GAME PROFILE
    // This is the first-party game with full native integration (Level 3)
    // ============================================================================

    'critcash_arena': {
        displayName: 'CritCash Arena',
        abbr: 'CCA',
        type: 'shooter',
        
        // Native integration - no Vision needed
        integrationLevel: 3, // Full integration
        integrationType: 'native',
        streamRequired: false,
        
        allowedModes: ['h2h', 'bracket', 'ranked', 'custom'],
        allowedPlatforms: ['PC', 'macOS'],
        
        // Game-specific config
        classes: ['Warrior', 'Wizard', 'Rogue', 'Paladin', 'Hunter', 'Warlock', 'Shaman'],
        
        formats: {
            'Duel (1v1)': {
                description: 'Pure skill test. First to eliminate wins.',
                durationMinutes: 5,
                teamSizes: [1],
                defaultTeamSize: 1,
                matchFormat: '1v1',
            },
            'Doubles (2v2)': {
                description: 'Partner synergy and accessible competition.',
                durationMinutes: 7,
                teamSizes: [2],
                defaultTeamSize: 2,
                matchFormat: '2v2',
            },
            'Skirmish (3v3)': {
                description: 'Classic arena format. Competitive standard.',
                durationMinutes: 8,
                teamSizes: [3],
                defaultTeamSize: 3,
                matchFormat: '3v3',
            },
            'Warzone (6v6)': {
                description: 'Large-scale team fights. Built for big tournaments.',
                durationMinutes: 12,
                teamSizes: [6],
                defaultTeamSize: 6,
                matchFormat: '6v6',
            },
            'King of the Hill': {
                description: 'Endless waves of minions and bosses. Survive and stand tall amongst your peers.',
                durationMinutes: 60,
                defaultTeamSize: 1,
                matchFormat: 'Survival',
            }
        },
        
        // Arena-specific settings
        roomSettings: {
            roomTypes: [
                { key: 'ranked', label: 'Ranked', description: 'ELO-based matchmaking with seasonal rewards.' },
                { key: 'cash', label: 'Crit Match', description: 'Costs Crits to enter. Winner earns the Crit pool.' },
                { key: 'custom', label: 'Custom', description: 'Private lobbies for practice or scrims.' },
            ],
            common: {
                map: {
                    label: 'Arena Map',
                    options: [
                        { key: 'castle', label: 'Infinity Castle' },
                        { key: 'icelands', label: 'Ice Lands' },
                        { key: 'grasslands', label: 'Grass Lands' },
                        { key: 'random', label: 'Random' },
                    ],
                    default: 'random',
                    description: 'Select the arena map for the match.',
                },
                spectators: {
                    label: 'Spectators',
                    options: [
                        { key: 'allow', label: 'Allow' },
                        { key: 'deny', label: 'Deny' },
                    ],
                    default: 'allow',
                    description: 'Allow spectators to watch the match.',
                },
            },
            ranked: {
                bracket: {
                    label: 'Rating Bracket',
                    options: [
                        { key: 'open', label: 'Open (Any Rating)' },
                        { key: 'bronze', label: 'Bronze (0-1199)' },
                        { key: 'silver', label: 'Silver (1200-1499)' },
                        { key: 'gold', label: 'Gold (1500-1799)' },
                        { key: 'platinum', label: 'Platinum (1800-2099)' },
                        { key: 'diamond', label: 'Diamond (2100+)' },
                    ],
                    default: 'open',
                },
            },
            cash: {
                entryFee: {
                    label: 'Entry (Crits)',
                    options: [
                        { key: '100', label: '1 Crit' },
                        { key: '500', label: '5 Crits' },
                        { key: '1000', label: '10 Crits' },
                        { key: '2500', label: '25 Crits' },
                        { key: '5000', label: '50 Crits' },
                        { key: '10000', label: '100 Crits' },
                    ],
                    default: '500',
                    description: 'Crit entry per player. The winner earns the Crit pool.',
                },
            },
        },
        
        // Roster tracking
        characters: {
            // Warriors (12)
            greystone: { name: 'Greystone', class: 'Warrior', archetype: 'Resilient Bruiser' },
            steel: { name: 'Steel', class: 'Warrior', archetype: 'Shield Tank' },
            grux: { name: 'Grux', class: 'Warrior', archetype: 'Pull Bruiser' },
            rampage: { name: 'Rampage', class: 'Warrior', archetype: 'Regen Tank' },
            khaimera: { name: 'Khaimera', class: 'Warrior', archetype: 'Dive Bruiser' },
            terra: { name: 'Terra', class: 'Warrior', archetype: 'Combo Bruiser' },
            sevarog: { name: 'Sevarog', class: 'Warrior', archetype: 'Scaling Tank' },
            crunch: { name: 'Crunch', class: 'Warrior', archetype: 'Combo Fighter' },
            feng_mao: { name: 'Feng Mao', class: 'Warrior', archetype: 'Burst Bruiser' },
            wukong: { name: 'Wukong', class: 'Warrior', archetype: 'Split Push' },
            riktor: { name: 'Riktor', class: 'Warrior', archetype: 'Hook Tank' },
            boris: { name: 'Boris', class: 'Warrior', archetype: 'Zone Control' },
            
            // Wizards (4)
            gideon: { name: 'Gideon', class: 'Wizard', archetype: 'Burst Mage' },
            gadget: { name: 'Gadget', class: 'Wizard', archetype: 'Zone Mage' },
            howitzer: { name: 'Howitzer', class: 'Wizard', archetype: 'Artillery Mage' },
            lt_belica: { name: 'Lt. Belica', class: 'Wizard', archetype: 'Anti-Mage' },
            
            // Rogues (4)
            countess: { name: 'Countess', class: 'Rogue', archetype: 'Burst Assassin' },
            kallari: { name: 'Kallari', class: 'Rogue', archetype: 'Stealth Assassin' },
            wraith: { name: 'Wraith', class: 'Rogue', archetype: 'Recon Assassin' },
            yin: { name: 'Yin', class: 'Rogue', archetype: 'Carry Assassin' },
            
            // Paladins (3)
            kwang: { name: 'Kwang', class: 'Paladin', archetype: 'Bruiser Support' },
            aurora: { name: 'Aurora', class: 'Paladin', archetype: 'CC Support' },
            serath: { name: 'Serath', class: 'Paladin', archetype: 'Carry Hybrid' },
            
            // Hunters (5)
            sparrow: { name: 'Sparrow', class: 'Hunter', archetype: 'Pure DPS' },
            twinblast: { name: 'Twinblast', class: 'Hunter', archetype: 'Mobile DPS' },
            grim_exe: { name: 'GRIM.exe', class: 'Hunter', archetype: 'Utility DPS' },
            drongo: { name: 'Drongo', class: 'Hunter', archetype: 'Trap DPS' },
            revenant: { name: 'Revenant', class: 'Hunter', archetype: 'Burst DPS' },
            
            // Warlocks (5)
            shinbi: { name: 'Shinbi', class: 'Warlock', archetype: 'DoT Assassin' },
            morigesh: { name: 'Morigesh', class: 'Warlock', archetype: 'Single-Target DoT' },
            the_fey: { name: 'The Fey', class: 'Warlock', archetype: 'Zone Control' },
            iggy_scorch: { name: 'Iggy & Scorch', class: 'Warlock', archetype: 'Turret Control' },
            zinx: { name: 'Zinx', class: 'Warlock', archetype: 'Tank Warlock' },
            
            // Shamans (4)
            narbash: { name: 'Narbash', class: 'Shaman', archetype: 'AoE Healer' },
            phase: { name: 'Phase', class: 'Shaman', archetype: 'Tether Healer' },
            muriel: { name: 'Muriel', class: 'Shaman', archetype: 'Shield Support' },
            dekker: { name: 'Dekker', class: 'Shaman', archetype: 'CC Support' },
        },
        
        // Launch roster (14 characters - 2 per class)
        launchRoster: [
            'greystone', 'steel',           // Warriors
            'gideon', 'gadget',             // Wizards
            'countess', 'kallari',          // Rogues
            'kwang', 'aurora',              // Paladins
            'sparrow', 'twinblast',         // Hunters
            'shinbi', 'morigesh',           // Warlocks
            'narbash', 'phase',             // Shamans
        ],
    },
}